import { useState, useRef, useEffect, useMemo } from "react";
import { applyFinderTag, isElectronApp, pickFolderWithRealPath } from "@/lib/finderTag";
import { format } from "date-fns";
import { CalendarIcon, Upload, Search, UserPlus, Users, X, FileText, CheckCircle2, FolderOpen, HardDrive, Cloud, Loader2, WifiOff, CheckCircle, MapPin, Check } from "lucide-react";
import GDriveFolderPicker from "@/components/GDriveFolderPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn, matchesSearch } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { toast } from "@/lib/toast";
import type { WizardState } from "@/pages/UploadWizard";
import { useAuth } from "@/hooks/useAuth";
import { useApplyDefaultLocation } from "@/hooks/useDefaultLocation";
import { DriveReauthDialog } from "@/components/DriveReauthDialog";
import { ProviderPicker } from "@/components/ProviderPicker";
import { saveHandle, loadHandle, deleteHandle } from "@/lib/sessionHandleStore";

const SUBJECT_LOCAL_HANDLE_KEY_PREFIX = "subject_local_";
const subjectLocalHandleKey = (folderId: string) => `${SUBJECT_LOCAL_HANDLE_KEY_PREFIX}${folderId}`;

interface PatientStepProps {
  wizardState: WizardState;
  onComplete: (state: Partial<WizardState>) => Promise<void>;
  isLoading: boolean;
  /**
   * What kind of subject this step is configuring. "patient" (default) uses the
   * existing *Patients Drive root and shows the New/Returning toggle.
   * "provider" sources from *Providers, hides the toggle, and relabels the form
   * for the Provider A-Roll flow.
   */
  subjectKind?: "patient" | "provider";
}

// File System Access API type augmentation
declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: string }) => Promise<FileSystemDirectoryHandle>;
  }
  // Chrome's permission API on FSA handles — not in the standard FSA spec, so
  // TS lib.dom doesn't ship these signatures.
  interface FileSystemHandle {
    queryPermission?: (descriptor?: { mode?: "read" | "readwrite" }) => Promise<PermissionState>;
    requestPermission?: (descriptor?: { mode?: "read" | "readwrite" }) => Promise<PermissionState>;
  }
}

function formatDateFolder(date: Date): string {
  return format(date, "MM.dd.yy");
}

/**
 * Verify a picked FileSystemDirectoryHandle is actually reachable on the
 * underlying filesystem. The handle can look valid in memory (and the picker
 * UI shows the right name) even when the underlying volume — e.g. a NAS share
 * — has been unmounted since it was chosen. Without this probe, the upload
 * step silently falls back to the browser's Downloads folder.
 *
 * The probe does two things:
 *   1. Re-checks (and if needed re-requests) readwrite permission.
 *   2. Pulls a single entry from the directory iterator, which forces the FSA
 *      layer to resolve the real path. A disconnected NAS throws here.
 *
 * Returns true only if the directory is both permitted and reachable.
 */
async function verifyFolderReachable(handle: FileSystemDirectoryHandle): Promise<boolean> {
  try {
    if (typeof handle.queryPermission === "function") {
      const current = await handle.queryPermission({ mode: "readwrite" });
      if (current !== "granted") {
        const requested = await handle.requestPermission?.({ mode: "readwrite" });
        if (requested !== "granted") return false;
      }
    }
    // Touch the underlying filesystem. We don't need to enumerate — pulling
    // the first entry (or hitting "done" on an empty folder) is enough.
    const iterator = (handle as unknown as { values: () => AsyncIterableIterator<FileSystemHandle> }).values();
    await iterator.next();
    return true;
  } catch (err) {
    console.warn("[PatientStep] Folder reachability probe failed:", err);
    return false;
  }
}

const NAS_HOST = "smb://192.168.1.254";

/**
 * Predicted NAS path for a subject in a region whose SMB share mirrors the
 * Drive layout. Currently only Iowa — Minnesota / Colorado return null so the
 * existing manual-pick UI stays unchanged.
 *
 * Patients: smb://192.168.1.254/Infinity/*Patients/{region}/{permission}/{name}
 * Providers: smb://192.168.1.254/Infinity/*Providers/{region}/{name}
 */
function buildNasPath(opts: {
  region: string | null | undefined;
  isProvider: boolean;
  name: string;
  permissionFolderName?: string | null;
  specialInstructions?: boolean;
}): { display: string; href: string } | null {
  if (opts.region !== "Iowa") return null;
  if (!opts.name) return null;
  const tail = [
    "Infinity",
    opts.isProvider ? "*Providers" : "*Patients",
    opts.region,
    ...(!opts.isProvider && opts.permissionFolderName ? [opts.permissionFolderName] : []),
    ...(opts.specialInstructions ? ["**Special Instructions"] : []),
    opts.name,
  ];
  return {
    display: [NAS_HOST, ...tail].join(" / ") + "/",
    href: NAS_HOST + "/" + tail.map(encodeURIComponent).join("/") + "/",
  };
}

export default function PatientStep({ wizardState, onComplete, isLoading, subjectKind = "patient" }: PatientStepProps) {
  const isProvider = subjectKind === "provider";
  // Folder-picking hints below the destinations; wording follows the admin
  // "Year folders for new uploads" switch.
  const { data: yearFoldersEnabled } = trpc.gdrive.yearFoldersEnabled.useQuery(undefined, {
    enabled: !isProvider,
  });
  const subjectLabel = isProvider ? "Provider" : "Patient";
  const subjectLabelLower = isProvider ? "provider" : "patient";
  const subjectFolderRoot = isProvider ? "*Providers" : "*Patients";
  // Provider A-Roll flow only supports the "returning" lookup — no new-subject creation.
  const [mode, setMode] = useState<"new" | "returning">(isProvider ? "returning" : wizardState.mode);

  // New patient state
  const [newPatientName, setNewPatientName] = useState(wizardState.patientName || "");
  const [consentFile, setConsentFile] = useState<File | null>(null);
  const [consentUploading, setConsentUploading] = useState(false);
  const [consentUploaded, setConsentUploaded] = useState(false);
  // Drive folder ID is captured after Google Drive folder creation for new patients
  const [newPatientDriveFolderId, setNewPatientDriveFolderId] = useState<string>(wizardState.gDriveFolderId ?? "");
  const [destinations, setDestinations] = useState<string[]>(
    wizardState.destinations && wizardState.destinations.length > 0
      ? wizardState.destinations
      : ["local"],
  );
  const [localFolderHandle, setLocalFolderHandle] = useState<FileSystemDirectoryHandle | null>(wizardState.localFolderHandle ?? null);
  const [localFolderPath, setLocalFolderPath] = useState<string>(wizardState.localFolderPath ?? "");
  // Real absolute path — only populated in the Electron desktop app via native folder picker
  const [localFolderRealPath, setLocalFolderRealPath] = useState<string | null>(null);
  // Returning patient real path
  const [returningLocalFolderRealPath, setReturningLocalFolderRealPath] = useState<string | null>(null);
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPickerLoading, setFolderPickerLoading] = useState(false);

  // Google Drive folder picker state
  const [gDrivePickerOpen, setGDrivePickerOpen] = useState(false);

  // Auto-reopen picker if we just returned from Google OAuth
  useEffect(() => {
    const pending = sessionStorage.getItem("gdrive_picker_pending");
    if (pending === "1") {
      sessionStorage.removeItem("gdrive_picker_pending");
      setGDrivePickerOpen(true);
    }
  }, []);
  const [gDriveFolderId, setGDriveFolderId] = useState<string>(wizardState.gDriveFolderId ?? "");
  const [gDriveFolderPath, setGDriveFolderPath] = useState<string>(wizardState.gDriveFolderPath ?? "");
  const [gDriveFolderColorLabel, setGDriveFolderColorLabel] = useState<string | null>(null);

  // Drive re-auth dialog state
  const [driveReauthOpen, setDriveReauthOpen] = useState(false);
  // Resolves the pending folder-creation promise after re-auth completes or is cancelled
  const driveReauthResolveRef = useRef<((reauthed: boolean) => void) | null>(null);

  /**
   * Returns true if the Drive token is valid, false if it needs re-auth.
   * If needs_reauth, opens the DriveReauthDialog and waits for the user to
   * re-connect or cancel before resolving.
   */
  const ensureDriveToken = async (): Promise<boolean> => {
    try {
      const result = await trpcUtils.gdrive.tokenStatus.fetch();
      if (result.status === "ok") return true;
      if (result.status === "no_token") return false; // never connected
    } catch {
      // Network error — assume ok so we don’t block unnecessarily
      return true;
    }
    // needs_reauth — show the dialog and wait
    return new Promise<boolean>((resolve) => {
      driveReauthResolveRef.current = resolve;
      setDriveReauthOpen(true);
    });
  };

  const { user } = useAuth();
  // Derive a stable device UUID (used as fallback when not logged in).
  // Stored in localStorage + cookie so it survives page refreshes.
  const deviceId = useMemo(() => {
    const key = "footage_device_id";
    let id = localStorage.getItem(key);
    if (!id) {
      const cookieMatch = document.cookie.match(/(?:^|;\s*)footage_device_id=([^;]+)/);
      id = cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
    }
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
      const expires = new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `footage_device_id=${encodeURIComponent(id)}; expires=${expires}; path=/; SameSite=Lax`;
    }
    return id;
  }, []);
  // The userOpenId variable is preserved (and still passed to child components
  // like the GDrive picker) for backwards compatibility, but the server now
  // identifies the user from the session cookie — these strings are only used
  // client-side as a stable key for caches.
  const userOpenId = user?.googleId ?? deviceId;
  const trpcUtils = trpc.useUtils();
  const createGDrivePatientFolders = trpc.gdrive.createPatientFolders.useMutation();
  // gdrive.uploadFile was removed when S3 was retired — uploads now happen
  // chunk-by-chunk via /api/drive-upload/* in FootageStep.
  const uploadConsentFormMutation = trpc.gdrive.uploadConsentForm.useMutation();

  // Check if Google Drive is connected in this browser
  const { data: gDriveStatusData, refetch: refetchGDriveStatus } = trpc.gdrive.status.useQuery(
    undefined,
    { enabled: !!user }
  );
  const isGDriveConnected = gDriveStatusData?.connected ?? false;

  // After OAuth callback returns with ?gdrive_connected=1, refetch status and reopen picker
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gdrive_connected") === "1") {
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
      refetchGDriveStatus();
    }
  }, [refetchGDriveStatus]);

  const handleGDriveConnect = () => {
    sessionStorage.setItem("gdrive_picker_pending", "1");
    const origin = window.location.origin;
    // Server reads the user from the session cookie.
    window.location.href = `/api/gdrive/auth?origin=${encodeURIComponent(origin)}`;
  };

  // Returning patient state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<{
    id: number;
    name: string;
    destinations: string[] | null;
    localFolderLabel?: string | null;
    gDriveFolderId?: string | null;
    gDriveFolderPath?: string | null;
    gDriveFolderColorLabel?: string | null;
    /** Region from Drive (Iowa / Minnesota / Colorado) */
    region?: string;
    /** Permission tier folder name (Pre-Approved / Check Before Posting / ...). Null for providers. */
    permissionFolderName?: string | null;
    /** Whether the subject lives under the **Special Instructions subfolder. */
    specialInstructions?: boolean;
  } | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const patientDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Returning patient: editable destination state (initialized from saved patient on selection)
  const [returningDestinations, setReturningDestinations] = useState<string[]>([]);
  const [returningLocalFolderHandle, setReturningLocalFolderHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [returningLocalFolderPath, setReturningLocalFolderPath] = useState("");
  const [returningGDriveFolderId, setReturningGDriveFolderId] = useState("");
  const [returningGDriveFolderPath, setReturningGDriveFolderPath] = useState("");
  const [returningFolderPickerLoading, setReturningFolderPickerLoading] = useState(false);

  // Shared
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isExam, setIsExam] = useState(false);
  // Provider A-Roll only: free-text "Video Topic" appended to the MM.DD.YY
  // folder name as " - {topic}". Patient workflow uses the Exam toggle
  // instead. Initialized from wizardState so a resumed Provider session
  // remembers the topic across "Add More This Session".
  const [videoTopic, setVideoTopic] = useState(wizardState.videoTopic ?? "");
  // Associated providers for this session (patient workflow only). Names are
  // chosen from the Provider libraries via autocomplete; stored as plain
  // names and stamped onto every footage row created this session.
  const [selectedProviders, setSelectedProviders] = useState<string[]>(wizardState.providers ?? []);
  // "No Associated Provider" confirm popup — shown by handleProceed when the
  // field is empty; lets the user add one or skip for the rare exceptions.
  const [noProviderDialogOpen, setNoProviderDialogOpen] = useState(false);
  // "Filmed by" — who this batch is attributed to. Defaults to the logged-in
  // user, but can be reassigned (e.g. Kyle uploading footage Chandler filmed).
  // Stamped onto every footage row (footage_files.uploaded_by_*) in FootageStep
  // and surfaced by the "Filmed by" filter in Footage Search.
  const filmersQuery = trpc.footage.listFilmers.useQuery();
  const [filmedByUserId, setFilmedByUserId] = useState<string>(
    wizardState.filmedByUserId ?? "",
  );
  // Default to the current user once auth resolves, unless a resumed session
  // already carried a choice.
  useEffect(() => {
    if (!filmedByUserId && user?.googleId) setFilmedByUserId(user.googleId);
  }, [user?.googleId, filmedByUserId]);
  const filmedByName =
    filmersQuery.data?.find((f) => f.googleId === filmedByUserId)?.name ??
    (filmedByUserId === user?.googleId ? user?.name ?? undefined : undefined);
  // Guard: prevent creating folders more than once if Proceed is clicked multiple times
  const [newPatientFoldersCreated, setNewPatientFoldersCreated] = useState(false);
  // Loading state for the Proceed button — set immediately on click to block double-clicks
  const [isProceeding, setIsProceeding] = useState(false);

  // Duplicate date folder guard
  const [duplicateDialog, setDuplicateDialog] = useState<{
    open: boolean;
    existingName: string;
    existingFolderId: string | null;
    suggestedName: string;
    /** Callback to proceed with the (possibly renamed) date folder, or use the existing folder directly */
    onConfirm: (dateFolderName: string, useExistingId?: string) => void;
  } | null>(null);

  const consentInputRef = useRef<HTMLInputElement>(null);
  const [consentDragOver, setConsentDragOver] = useState(false);

  const handleConsentDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setConsentDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please drop a PDF file for the consent form");
      return;
    }
    setConsentFile(file);
    setConsentUploaded(false);
  };

  const handleConsentDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setConsentDragOver(true);
  };

  const handleConsentDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setConsentDragOver(false);
  };

  // Location filter for the Returning Patient lookup. Null until the user
  // picks one; "all" triggers the full cross-region fetch (slower).
  const [returningLocationFilter, setReturningLocationFilter] = useState<
    "Iowa" | "Minnesota" | "Colorado" | "all" | null
  >(null);
  useApplyDefaultLocation(returningLocationFilter, setReturningLocationFilter);

  // Drive-sourced subject list — only fetched once a Location filter is set.
  // Patient and Provider routes have identical shapes; only the Drive root differs.
  const driveQueryInput =
    returningLocationFilter && returningLocationFilter !== "all"
      ? { region: returningLocationFilter }
      : {};
  const driveQueryEnabled =
    mode === "returning" && isGDriveConnected && returningLocationFilter !== null;
  const drivePatientsQuery = trpc.gdrive.listDrivePatients.useQuery(driveQueryInput, {
    enabled: driveQueryEnabled && !isProvider,
  });
  const driveProvidersQuery = trpc.gdrive.listDriveProviders.useQuery(driveQueryInput, {
    enabled: driveQueryEnabled && isProvider,
  });
  const drivePatientsRaw = (isProvider ? driveProvidersQuery.data : drivePatientsQuery.data) ?? [];
  const isDrivePatientsLoading = isProvider
    ? driveProvidersQuery.isLoading
    : drivePatientsQuery.isLoading;
  // Filter Drive patients by search query
  const drivePatients = searchQuery.trim()
    ? drivePatientsRaw.filter((p) => matchesSearch(p.name, searchQuery))
    : drivePatientsRaw;

  // No DB patient mutations — Drive folder ID is the patient identifier

  // File System Access API — supported in Chrome/Edge 86+, NOT in Firefox or Safari
  const isFsaSupported = typeof window !== "undefined" && typeof (window as Window & { showDirectoryPicker?: unknown }).showDirectoryPicker === "function";

  // Persisted FileSystemDirectoryHandle for the selected subject. When the
  // user previously picked a local folder for this Drive folder ID, we recall
  // the handle from IndexedDB on selection. queryPermission tells us whether
  // we can apply it silently or need a user-gesture click to re-grant.
  const [pendingPersistedHandle, setPendingPersistedHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [reconnectInFlight, setReconnectInFlight] = useState(false);

  // Predicted SMB path for the currently-selected subject. Iowa only; null
  // elsewhere. Browsers can't auto-mount SMB, so we surface this as a Finder
  // hint the user can click to open the share.
  const selectedNasPath = useMemo(
    () =>
      selectedPatient
        ? buildNasPath({
            region: selectedPatient.region,
            isProvider,
            name: selectedPatient.name,
            permissionFolderName: selectedPatient.permissionFolderName,
            specialInstructions: selectedPatient.specialInstructions,
          })
        : null,
    [selectedPatient, isProvider],
  );

  // Auto-restore (or queue-for-reconnect) a previously-saved folder handle
  // when a returning subject is selected. Chrome typically resets FSA
  // permissions per session, so "granted" is rare; "prompt" is the common
  // case and we surface a Reconnect button.
  //
  // Provider A-Roll opts out: the user wants to manually pick a fresh
  // local folder every session (the picked folder is the final destination,
  // not a nested PatientName/MM.DD.YY tree), so we deliberately skip the
  // saved-handle recall here.
  useEffect(() => {
    const folderId = selectedPatient?.gDriveFolderId;
    if (mode !== "returning" || !folderId || isProvider) {
      setPendingPersistedHandle(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stored = await loadHandle(subjectLocalHandleKey(folderId));
        if (cancelled || !stored) {
          setPendingPersistedHandle(null);
          return;
        }
        const perm = stored.queryPermission
          ? await stored.queryPermission({ mode: "readwrite" })
          : "prompt";
        if (cancelled) return;
        if (perm === "granted") {
          setReturningLocalFolderHandle(stored);
          setReturningLocalFolderPath(stored.name);
          setReturningDestinations((prev) =>
            prev.includes("local") ? prev : [...prev, "local"],
          );
          setPendingPersistedHandle(null);
          toast.success(`Auto-reconnected to ${stored.name}/`);
        } else if (perm === "prompt") {
          setPendingPersistedHandle(stored);
        } else {
          // denied → drop the stale handle so we don't keep prompting
          setPendingPersistedHandle(null);
          await deleteHandle(subjectLocalHandleKey(folderId)).catch(() => {});
        }
      } catch {
        if (!cancelled) setPendingPersistedHandle(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPatient?.gDriveFolderId, mode, isProvider]);

  const toggleDestination = async (id: string) => {
    const isAdding = !destinations.includes(id);
    if (id === "local" && isAdding) {
      // Open folder picker immediately when checking Local Drive
      await pickLocalFolder();
      // pickLocalFolder adds to destinations on success
      return;
    }
    if (id === "google_drive" && isAdding) {
      // Add destination first, then open folder picker
      setDestinations((prev) => (prev.includes("google_drive") ? prev : [...prev, "google_drive"]));
      setGDrivePickerOpen(true);
      return;
    }
    setDestinations((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleGDriveFolderSelect = async (folderId: string, folderPath: string, colorLabel: string | null) => {
    if (mode === "returning") {
      // In returning mode, update the returning-specific Drive state
      setReturningGDriveFolderId(folderId);
      setReturningGDriveFolderPath(folderPath);
      toast.success(`Google Drive folder updated: ${folderPath}`);
      return;
    }
    setGDriveFolderId(folderId);
    setGDriveFolderPath(folderPath);
    setGDriveFolderColorLabel(colorLabel);
    // Drive folder ID is the patient identifier — no DB persistence needed here
    if (false) {
      try {
        // placeholder to preserve block structure
        void userOpenId;
      } catch {
        // Non-fatal — will be saved on proceed
      }
    }
    toast.success(`Google Drive destination set: ${folderPath}`);
  };

  const removeGDriveDestination = () => {
    setDestinations((prev) => prev.filter((d) => d !== "google_drive"));
    setGDriveFolderId("");
    setGDriveFolderPath("");
  };

  const pickLocalFolder = async (patientName?: string) => {
    if (!isFsaSupported) {
      setDestinations((prev) => (prev.includes("local") ? prev : [...prev, "local"]));
      toast.info("Folder picker not supported in this browser — files will be downloaded normally");
      return null;
    }
    setFolderPickerLoading(true);
    try {
      // Browser-only: there's no native dialog to ask for an absolute path.
      // We keep the variable so the rest of the function still type-checks.
      const realPath: string | null = null;
      const handle = await window.showDirectoryPicker!({ mode: "readwrite" });
      setLocalFolderHandle(handle);
      setLocalFolderPath(handle.name);
      if (realPath) {
        // Store the real path, appending the patient subfolder name if provided
        const patientSuffix = patientName ? `/${patientName.replace(/[<>:"/\\|?]/g, "_").trim()}` : "";
        setLocalFolderRealPath(realPath + patientSuffix);
      }
      setDestinations((prev) => (prev.includes("local") ? prev : [...prev, "local"]));
      toast.success(`Root folder selected: ${handle.name}`);
      return handle;
    } catch (err: unknown) {
      if ((err as Error)?.name !== "AbortError") {
        toast.error("Could not access the selected folder");
      }
      return null;
    } finally {
      setFolderPickerLoading(false);
    }
  };

  /** Create PatientName/ subfolder inside the root handle, return the subfolder handle */
  const createPatientFolder = async (
    rootHandle: FileSystemDirectoryHandle,
    patientName: string
  ): Promise<FileSystemDirectoryHandle | null> => {
    try {
      const sanitized = patientName.replace(/[<>:"/\\|?]/g, "_").trim();
      const folderHandle = await rootHandle.getDirectoryHandle(sanitized, { create: true });
      return folderHandle;
    } catch {
      toast.error("Could not create patient folder on your drive");
      return null;
    }
  };

  /**
   * Write the consent form PDF into the given folder handle using File System Access API.
   * Falls back to a browser download if FSA write is unavailable.
   */
  const saveConsentFormToFolder = async (
    folderHandle: FileSystemDirectoryHandle,
    file: File
  ): Promise<void> => {
    const fsaWriteAvailable =
      typeof folderHandle.getFileHandle === "function" &&
      typeof window !== "undefined" &&
      typeof (window as Window & { showDirectoryPicker?: unknown }).showDirectoryPicker === "function";

    if (fsaWriteAvailable) {
      try {
        const fileHandle = await folderHandle.getFileHandle(file.name, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(file);
        await writable.close();
        toast.success(`Consent form saved to patient folder: ${file.name}`);
        return;
      } catch {
        // FSA write failed — fall through to download fallback
      }
    }
    // Universal fallback: trigger a browser download
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 200);
    toast.info(`Consent form downloaded — place it in the ${folderHandle.name}/ folder`);
  };

  const removeLocalDestination = () => {
    setDestinations((prev) => prev.filter((d) => d !== "local"));
    setLocalFolderHandle(null);
    setLocalFolderPath("");
  };

  const handleConsentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Please upload a PDF file for the consent form");
        return;
      }
      setConsentFile(file);
    }
  };

  /** Convert a File to base64 using FileReader — works universally across all browsers */
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // result is "data:<mime>;base64,<data>" — strip the prefix
        resolve(result.split(",")[1] ?? "");
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  // Consent form is saved to local folder and/or uploaded to Drive directly — no DB call needed
  const handleConsentUpload = async (): Promise<{ name: string; mimeType: string } | null> => {
    if (!consentFile) return null;
    setConsentUploading(true);
    try {
      setConsentUploaded(true);
      toast.success("Consent form ready to upload");
      return { name: consentFile.name, mimeType: consentFile.type };
    } catch {
      toast.error("Failed to prepare consent form");
      return null;
    } finally {
      setConsentUploading(false);
    }
  };

  const handleProceed = async (skipProviderCheck = false) => {
    if (isProceeding) return; // block double-click
    setIsProceeding(true);
    // The MM.DD.YY folder is now created later in FootageStep, after we read
    // each file's creation date from its metadata. Passing dateFolder="" is
    // the signal to the next step that auto-detect is active. The Exam flag
    // is carried through and appended to the folder name at materialization.
    const dateFolder = "";

    if (mode === "new") {
      if (!newPatientName.trim()) {
        toast.error("Please enter the patient name");
        setIsProceeding(false);
        return;
      }
      // Associated Provider is expected for patient footage — it feeds the
      // per-provider counts on the Reports page. Rare exceptions (e.g.
      // parent-filmed home footage) can skip via the confirm popup.
      if (!isProvider && selectedProviders.length === 0 && !skipProviderCheck) {
        setNoProviderDialogOpen(true);
        setIsProceeding(false);
        return;
      }
      if (destinations.length === 0) {
        toast.error("Please select at least one upload destination");
        setIsProceeding(false);
        return;
      }
      if (destinations.includes("local") && !localFolderHandle) {
        toast.error("Please select a destination folder or uncheck Local Drive to continue");
        setIsProceeding(false);
        return;
      }
      if (destinations.includes("local") && localFolderHandle) {
        const reachable = await verifyFolderReachable(localFolderHandle);
        if (!reachable) {
          toast.error(
            `Cannot reach "${localFolderPath}" — verify the drive or NAS is connected, then try again`,
            { duration: 10000 },
          );
          setIsProceeding(false);
          return;
        }
      }
      // No standalone region validation needed — Location is selected inside the folder picker modal

      // Upload consent form info if provided (no DB call — Drive upload handled below)
      let consentS3Result: { name: string; mimeType: string } | null = null;
      if (consentFile && !consentUploaded) {
        consentS3Result = await handleConsentUpload();
      }

      // Create PatientName/ subfolder inside the chosen root folder (only once — guard against double-click)
      let patientFolderHandle: FileSystemDirectoryHandle | null = null;
      let gDriveDateFolderId: string | undefined;
      let finalGDriveFolderId = gDriveFolderId || undefined;
      let finalGDriveFolderPath = gDriveFolderPath || undefined;

      if (!newPatientFoldersCreated) {
        const rootHandle = localFolderHandle;
        if (rootHandle) {
          patientFolderHandle = await createPatientFolder(rootHandle, newPatientName.trim());
          if (patientFolderHandle) {
            toast.success(`Created folder: ${rootHandle.name}/${newPatientName.trim()}/`);
            // Save consent form PDF into the patient folder
            if (consentFile) {
              await saveConsentFormToFolder(patientFolderHandle, consentFile);
            }
            // Apply macOS Finder color tag (Electron desktop app only)
            if (gDriveFolderColorLabel) {
              const tagPath = localFolderRealPath
                ? localFolderRealPath.endsWith(newPatientName.trim().replace(/[<>:"/\\|?]/g, "_"))
                  ? localFolderRealPath
                  : `${localFolderRealPath}/${newPatientName.trim().replace(/[<>:"/\\|?]/g, "_")}`
                : null;
              if (tagPath) {
                await applyFinderTag(tagPath, gDriveFolderColorLabel);
              }
            }
          }
        }

        // Create Google Drive folders (PatientName/ and MM.DD.YY/)
        if (destinations.includes("google_drive") && isGDriveConnected) {
          // Check token validity before attempting folder creation
          const tokenOk = await ensureDriveToken();
          if (!tokenOk) {
            toast.warning("Google Drive skipped — re-authentication cancelled. Files will be saved locally only.");
          } else
          try {
            const parentFolderId = gDriveFolderId || "18ak3Sn8S2L5zVRUXxGoRZ3xUCn1bf-oH";
            // Create only the PatientName/ folder upfront — the MM.DD.YY/
            // subfolder is created later in FootageStep, once we've read
            // each file's creation date from its metadata.
            const result = await createGDrivePatientFolders.mutateAsync({
              patientName: newPatientName.trim(),
              parentFolderId,
              colorLabel: gDriveFolderColorLabel,
            });
            gDriveDateFolderId = result.dateFolderId; // undefined here — set in FootageStep
            finalGDriveFolderId = result.patientFolderId;
            const pickerBase = gDriveFolderPath ? gDriveFolderPath.replace(/\/$/, "") : "*Patients";
            finalGDriveFolderPath = `${pickerBase}/${newPatientName.trim()}/`;
            toast.success(`Google Drive: Created folder ${finalGDriveFolderPath}`);
            // Upload consent form PDF directly to the patient folder (no S3 involved)
            if (consentFile && finalGDriveFolderId) {
              try {
                const formData = new FormData();
                formData.append("file", consentFile, consentFile.name);
                formData.append("driveFolderId", finalGDriveFolderId);
                formData.append("filename", consentFile.name);
                const resp = await fetch("/api/consent-upload", { method: "POST", body: formData });
                if (!resp.ok) {
                  const errBody = await resp.json().catch(() => ({ error: resp.statusText }));
                  throw new Error(errBody.error ?? resp.statusText);
                }
                toast.success("Consent form uploaded to Google Drive");
              } catch (consentErr: any) {
                const errMsg = consentErr?.message ?? String(consentErr);
                console.warn("[GDrive] Consent form upload failed:", errMsg);
                toast.error(`Consent form Drive upload failed: ${errMsg}`, { duration: 10000 });
              }
            }
          } catch (err) {
            console.warn("[GDrive] Folder creation failed:", err);
            toast.error("Google Drive folder creation failed — footage will still be uploaded and saved");
          }
        }

        setNewPatientFoldersCreated(true);
      }

      setIsProceeding(false);
      await onComplete({
        mode: "new",
        driveFolderId: finalGDriveFolderId,
        patientName: newPatientName.trim(),
        dateFolder,
        isExam,
        videoTopic: videoTopic.trim() || undefined,
        providers: isProvider ? undefined : selectedProviders,
        filmedByUserId: filmedByUserId || user?.googleId || undefined,
        filmedByName,
        subjectKind: isProvider ? "provider" : "patient",
        destinations,
        consentFormUploaded: consentUploaded || !!consentFile,
        localFolderHandle,
        localFolderPath,
        patientFolderHandle,
        localFolderLabel: localFolderPath,
        gDriveFolderId: finalGDriveFolderId,
        gDriveFolderPath: finalGDriveFolderPath,
        gDriveDateFolderId,
      });
    } else {
        // Returning patient
      if (!selectedPatient) {
        toast.error(`Please select a ${subjectLabelLower}`);
        setIsProceeding(false);
        return;
      }
      // Associated Provider is expected for patient footage — it feeds the
      // per-provider counts on the Reports page. Rare exceptions (e.g.
      // parent-filmed home footage) can skip via the confirm popup.
      if (!isProvider && selectedProviders.length === 0 && !skipProviderCheck) {
        setNoProviderDialogOpen(true);
        setIsProceeding(false);
        return;
      }
      // Validate at least one destination
      if (returningDestinations.length === 0) {
        toast.error("Please select at least one upload destination");
        setIsProceeding(false);
        return;
      }
      // Validate Google Drive is connected if selected
      if (returningDestinations.includes("google_drive") && !isGDriveConnected) {
        toast.error("Please connect Google Drive before proceeding");
        setIsProceeding(false);
        return;
      }
      // Video Topic is OPTIONAL for Provider A-Roll. If the user leaves it
      // blank, FootageStep runs an AI suggestion after all clips finish
      // transcribing and offers it in a confirmation dialog before
      // navigating to the success page.

      const effectiveFolderLabel = returningLocalFolderPath || selectedPatient.localFolderLabel || "";

      // For returning patients with local drive: resolve the root folder
      // handle in priority order:
      //   1. Already in-memory (user picked or auto-restored this session)
      //   2. Saved handle from a prior session — try requestPermission first
      //      (single Allow prompt, no directory dialog)
      //   3. Fresh OS directory picker
      // Cancel/deny at any stage aborts the wizard advance rather than
      // silently proceeding with a missing handle (which would skip local
      // writes without any feedback).
      let resolvedPatientFolderHandle: FileSystemDirectoryHandle | null = null;
      // Provider workflow needs the picked root handle threaded through as
      // localFolderHandle (FootageStep's baseLocalHandle fallback) since we
      // deliberately leave patientFolderHandle null. Captured outside the
      // block so the onComplete payload below can read it.
      let providerLocalRootHandle: FileSystemDirectoryHandle | null = null;
      if (returningDestinations.includes("local") && isFsaSupported) {
        let rootHandle: FileSystemDirectoryHandle | null = returningLocalFolderHandle;
        if (!rootHandle && pendingPersistedHandle) {
          try {
            const perm = pendingPersistedHandle.requestPermission
              ? await pendingPersistedHandle.requestPermission({ mode: "readwrite" })
              : "denied";
            if (perm === "granted") {
              rootHandle = pendingPersistedHandle;
              setReturningLocalFolderHandle(pendingPersistedHandle);
              setReturningLocalFolderPath(pendingPersistedHandle.name);
              setPendingPersistedHandle(null);
              toast.success(`Reconnected to ${pendingPersistedHandle.name}/`);
            }
          } catch {
            // fall through to manual picker
          }
        }
        if (!rootHandle) {
          rootHandle = await pickLocalFolder(selectedPatient.name);
          if (rootHandle) {
            setReturningLocalFolderHandle(rootHandle);
            setReturningLocalFolderPath(rootHandle.name);
            setPendingPersistedHandle(null);
          }
        }
        if (!rootHandle) {
          toast.error(
            isProvider
              ? "Pick a local folder to continue — Local Drive is required for Provider A-Roll"
              : "Pick a local folder or uncheck Local Drive to continue",
          );
          setIsProceeding(false);
          return;
        }
        // Verify the folder is actually reachable — a saved handle can point
        // at an unmounted NAS share; without this probe the upload step would
        // silently fall back to Downloads.
        const reachable = await verifyFolderReachable(rootHandle);
        if (!reachable) {
          toast.error(
            `Cannot reach "${rootHandle.name}" — verify the drive or NAS is connected, then try again`,
            { duration: 10000 },
          );
          setIsProceeding(false);
          return;
        }
        // Provider A-Roll: skip the persisted-handle and subject-subfolder
        // logic entirely. The picked folder IS the final local destination —
        // no PatientName/ wrapper, no MM.DD.YY/ wrapper, files save flat
        // (FootageStep's resolveLocalDateFolder short-circuits when
        // subjectKind === "provider"). We also don't save the handle to
        // IndexedDB so the next session forces a fresh manual pick.
        if (isProvider) {
          resolvedPatientFolderHandle = null;
        } else {
          // Persist the (possibly freshly-picked) handle so next session can
          // auto-reconnect instead of re-prompting.
          if (selectedPatient.gDriveFolderId) {
            await saveHandle(subjectLocalHandleKey(selectedPatient.gDriveFolderId), rootHandle).catch(() => {});
          }
          const sanitizedName = selectedPatient.name.replace(/[<>:"/\\|?]/g, "_").trim();
          if (rootHandle.name === sanitizedName || rootHandle.name === selectedPatient.name) {
            resolvedPatientFolderHandle = rootHandle;
          } else {
            resolvedPatientFolderHandle = await createPatientFolder(rootHandle, selectedPatient.name);
          }
          if (!resolvedPatientFolderHandle) {
            // createPatientFolder already toasted the failure
            setIsProceeding(false);
            return;
          }
          // Apply macOS Finder color tag (Electron desktop app only).
          const returningColorLabel = selectedPatient.gDriveFolderColorLabel ?? null;
          const returningTagPath = returningLocalFolderRealPath ||
            (localFolderRealPath && resolvedPatientFolderHandle
              ? `${localFolderRealPath}/${sanitizedName}`
              : null);
          if (returningTagPath && returningColorLabel) {
            await applyFinderTag(returningTagPath, returningColorLabel);
          }
        }
        // Provider path: capture the picked root handle for the onComplete
        // payload below — it'll go through as localFolderHandle so
        // FootageStep's baseLocalHandle fallback resolves to it.
        if (isProvider) {
          providerLocalRootHandle = rootHandle;
          setReturningLocalFolderHandle(rootHandle);
        }
      }

      // Resolve effective Drive folder ID: prefer any updated folder from the destination section
      const effectiveDriveFolderId = returningGDriveFolderId || selectedPatient.gDriveFolderId || undefined;
      const effectiveDriveFolderPath = returningGDriveFolderPath || selectedPatient.gDriveFolderPath || undefined;

      // Drive folder ID is the patient identifier — no DB patient record needed

      // Date subfolder is created later in FootageStep, after we read each
      // file's creation date from its metadata. Token validity is still
      // checked there before any folder is created.

      // Reset the spinner state before handing off, so the button doesn't
      // stay stuck on "Creating folders…" if the wizard handler short-
      // circuits (e.g. validation failure in handlePatientStepComplete).
      setIsProceeding(false);
      try {
        await onComplete({
          mode: "returning",
          driveFolderId: effectiveDriveFolderId,
          patientName: selectedPatient.name,
          dateFolder,
          isExam,
          videoTopic: videoTopic.trim() || undefined,
          providers: isProvider ? undefined : selectedProviders,
          filmedByUserId: filmedByUserId || user?.googleId || undefined,
          filmedByName,
          subjectKind: isProvider ? "provider" : "patient",
          destinations: returningDestinations,
          consentFormUploaded: true,
          // Provider workflow: pass the picked root handle through here
          // since patientFolderHandle is intentionally null (no subject
          // subfolder). Patient workflow keeps the original (null for
          // returning patients — baseLocalHandle falls back to
          // patientFolderHandle).
          localFolderHandle: isProvider ? providerLocalRootHandle : localFolderHandle,
          localFolderPath,
          patientFolderHandle: resolvedPatientFolderHandle,
          localFolderLabel: effectiveFolderLabel,
          gDriveFolderId: effectiveDriveFolderId,
          gDriveFolderPath: effectiveDriveFolderPath,
          gDriveDateFolderId: undefined,
        });
      } catch (err) {
        // onComplete owns its own error toasts; just don't trap the spinner.
        console.warn("[PatientStep] onComplete (returning) threw:", err);
      }
    }
  };

  const selectPatient = (patient: {
    id: number;
    name: string;
    destinations: string[] | null;
    localFolderLabel?: string | null;
    gDriveFolderId?: string | null;
    gDriveFolderPath?: string | null;
    gDriveFolderColorLabel?: string | null;
    region?: string;
    permissionFolderName?: string | null;
    specialInstructions?: boolean;
  }) => {
    setSelectedPatient(patient);
    setSearchQuery(patient.name);
    setShowDropdown(false);
    // Initialize editable returning destination state from saved record
    setReturningDestinations(patient.destinations ?? []);
    setReturningLocalFolderHandle(null);
    setReturningLocalFolderPath("");
    setReturningGDriveFolderId(patient.gDriveFolderId ?? "");
    setReturningGDriveFolderPath(patient.gDriveFolderPath ?? "");
  };

  /** Select a patient sourced from Google Drive folder listing */
  const selectDrivePatient = (dp: {
    name: string;
    folderId: string;
    region: string;
    regionFolderId: string;
    permissionFolderName: string | null;
    specialInstructions: boolean;
  }) => {
    const pathSegments = ["My Drive", "Infinity", subjectFolderRoot, dp.region];
    if (dp.permissionFolderName) pathSegments.push(dp.permissionFolderName);
    if (dp.specialInstructions) pathSegments.push("**Special Instructions");
    pathSegments.push(`${dp.name}/`);
    selectPatient({
      id: -1, // sentinel: Drive-sourced, no local DB record
      name: dp.name,
      // Pre-select both destinations — the user can uncheck either if this
      // session is Drive-only or local-only. Local needs the folder picked
      // before Proceed; Google Drive's folder is auto-populated from dp.
      destinations: ["google_drive", "local"],
      localFolderLabel: null,
      gDriveFolderId: dp.folderId,
      gDriveFolderPath: pathSegments.join(" / "),
      region: dp.region,
      permissionFolderName: dp.permissionFolderName,
      specialInstructions: dp.specialInstructions,
    });
  };

  return (
    <div className="space-y-6">
      {/* Drive re-auth dialog — shown when token is expired/revoked before folder creation */}
      <DriveReauthDialog
        open={driveReauthOpen}
        onSuccess={() => {
          setDriveReauthOpen(false);
          // Small delay to let the token be stored before we retry
          setTimeout(() => {
            driveReauthResolveRef.current?.(true);
            driveReauthResolveRef.current = null;
          }, 800);
        }}
        onCancel={() => {
          setDriveReauthOpen(false);
          driveReauthResolveRef.current?.(false);
          driveReauthResolveRef.current = null;
        }}
      />
      {/* Google Drive Folder Picker Modal */}
      <GDriveFolderPicker
        open={gDrivePickerOpen}
        onClose={() => setGDrivePickerOpen(false)}
        userOpenId={userOpenId}
        onSelect={handleGDriveFolderSelect}
        selectedFolderId={gDriveFolderId || undefined}
        selectedColorLabel={(gDriveFolderColorLabel as import("@/components/GDriveFolderPicker").FolderColorLabel | null) ?? null}
        requireColorLabel={!isProvider && mode === "new"}
        subjectKind={subjectKind}
      />
      {/* No Associated Provider confirm — add one, or skip for rare
          exceptions like parent-filmed home footage. */}
      <AlertDialog open={noProviderDialogOpen} onOpenChange={setNoProviderDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No Associated Provider</AlertDialogTitle>
            <AlertDialogDescription>
              This batch doesn&apos;t have an Associated Provider, so its clips will show as
              &ldquo;No provider tagged&rdquo; on the Provider Clips report. Skip only for rare
              cases like parent-provided footage from home.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Go Back &amp; Add a Provider</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setNoProviderDialogOpen(false);
                void handleProceed(true);
              }}
            >
              Skip &amp; Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Date Folder Warning Dialog */}
      {duplicateDialog && (
        <AlertDialog open={duplicateDialog.open} onOpenChange={(open) => {
          if (!open) {
            // User dismissed without choosing — treat as cancel
            duplicateDialog.onConfirm(duplicateDialog.existingName);
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Date Folder Already Exists</AlertDialogTitle>
              <AlertDialogDescription>
                A folder named <strong>{duplicateDialog.existingName}</strong> already exists in this patient's Drive folder.
                Choose how to proceed:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <AlertDialogCancel onClick={() => {
                setDuplicateDialog(null);
              }}>
                Cancel
              </AlertDialogCancel>
              {duplicateDialog.existingFolderId && (
                <AlertDialogAction
                  onClick={() => {
                    duplicateDialog.onConfirm(duplicateDialog.existingName, duplicateDialog.existingFolderId!);
                  }}
                >
                  Use Existing Folder
                </AlertDialogAction>
              )}
              <AlertDialogAction
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  duplicateDialog.onConfirm(duplicateDialog.suggestedName);
                }}
              >
                Create "{duplicateDialog.suggestedName}"
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {/* Mode Toggle — hidden in Provider A-Roll flow (lookup only) */}
      {!isProvider && (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl">Step 1: Patient Setup</CardTitle>
          <CardDescription>Select whether this is a new or returning patient</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMode("new")}
              className={cn(
                "flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left",
                mode === "new"
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border hover:border-primary/40 hover:bg-muted/50"
              )}
            >
              <div
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                  mode === "new" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}
              >
                <UserPlus className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">New Patient</div>
                <div className="text-xs text-muted-foreground mt-0.5">Create a new patient record</div>
              </div>
            </button>

            <button
              onClick={() => setMode("returning")}
              className={cn(
                "flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left",
                mode === "returning"
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border hover:border-primary/40 hover:bg-muted/50"
              )}
            >
              <div
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                  mode === "returning" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}
              >
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">Returning Patient</div>
                <div className="text-xs text-muted-foreground mt-0.5">Select from existing records</div>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* New Patient Form */}
      {mode === "new" && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary" />
              New Patient Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Patient Name */}
            <div className="space-y-2">
              <Label htmlFor="patient-name" className="text-sm font-medium">
                Patient Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="patient-name"
                placeholder="Enter patient name"
                value={newPatientName}
                onChange={(e) => setNewPatientName(e.target.value)}
                className="h-10"
                autoComplete="off"
              />
              {newPatientName && (
                <p className="text-xs text-muted-foreground">
                  Main folder: <span className="font-medium text-foreground">{newPatientName}/</span>
                </p>
              )}
            </div>

            {/* Consent Form Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Photo & Consent Form <span className="text-muted-foreground text-xs">(PDF)</span>
              </Label>
              <input
                ref={consentInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleConsentFileChange}
                className="hidden"
              />
              {!consentFile ? (
                <div
                  onDrop={handleConsentDrop}
                  onDragOver={handleConsentDragOver}
                  onDragLeave={handleConsentDragLeave}
                  onClick={() => consentInputRef.current?.click()}
                  className={cn(
                    "w-full flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all text-center select-none",
                    consentDragOver
                      ? "border-primary bg-primary/8 scale-[1.01]"
                      : "border-border hover:border-primary/50 hover:bg-muted/30"
                  )}
                >
                  <div className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center transition-colors",
                    consentDragOver ? "bg-primary/15" : "bg-muted"
                  )}>
                    <FileText className={cn("w-5 h-5 transition-colors", consentDragOver ? "text-primary" : "text-muted-foreground")} />
                  </div>
                  <div>
                    <div className={cn("text-sm font-medium transition-colors", consentDragOver ? "text-primary" : "text-foreground")}>
                      {consentDragOver ? "Drop PDF here" : "Upload consent form PDF"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {consentDragOver ? "Release to attach" : "Drag & drop or click to browse"}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl border border-border">
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    {consentUploaded ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <FileText className="w-4 h-4 text-emerald-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{consentFile.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {consentUploaded
                        ? "Uploaded successfully"
                        : `Ready to upload · ${consentFile.size < 1024 * 1024
                            ? `${Math.round(consentFile.size / 1024)} KB`
                            : `${(consentFile.size / (1024 * 1024)).toFixed(1)} MB`}`}
                    </div>
                  </div>
                  {!consentUploaded && (
                    <button
                      onClick={() => {
                        setConsentFile(null);
                        if (consentInputRef.current) consentInputRef.current.value = "";
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Associated Provider(s) — patient workflow only. Sits between
                the Photo & Consent Form section and the Exam toggle. */}
            {!isProvider && (
              <ProviderAssociationField value={selectedProviders} onChange={setSelectedProviders} />
            )}

            {/* Session date is auto-detected from each file's metadata in
                FootageStep. The Exam toggle is the only date-related decision
                that still lives here; it appends " - exam" to the resulting
                MM.DD.YY folder name. */}
            <ExamFootageToggle isExam={isExam} onExamChange={setIsExam} showExam={!isProvider} />
            <VideoTopicField
              videoTopic={videoTopic}
              onVideoTopicChange={setVideoTopic}
              showTopic={isProvider}
            />
            <FilmedByField
              value={filmedByUserId}
              onChange={setFilmedByUserId}
              options={filmersQuery.data ?? []}
              currentUserId={user?.googleId}
              loading={filmersQuery.isLoading}
            />

            {/* Destinations */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Upload Destinations <span className="text-destructive">*</span>
              </Label>
              {!isProvider && (
                <p className="text-xs text-muted-foreground">
                  For each destination, choose the <strong>permission folder</strong> (e.g. Pre-Approved). The
                  app creates the patient&apos;s name folder{yearFoldersEnabled ? ", year folder," : ""} and
                  date folder for you.
                </p>
              )}
              <div className="space-y-2">

                {/* ── Google Drive ── */}
                <div
                  className={cn(
                    "rounded-xl border transition-all",
                    destinations.includes("google_drive")
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  )}
                >
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer"
                    onClick={() => {
                      if (destinations.includes("google_drive") && isGDriveConnected) {
                        // Card body click when already set: re-open folder picker
                        setGDrivePickerOpen(true);
                      } else if (!destinations.includes("google_drive")) {
                        toggleDestination("google_drive");
                      }
                    }}
                  >
                    <Checkbox
                      checked={destinations.includes("google_drive")}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          removeGDriveDestination();
                        } else {
                          toggleDestination("google_drive");
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Cloud className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        Google Drive
                        {destinations.includes("google_drive") && (
                          isGDriveConnected
                            ? <span className="flex items-center gap-1 text-xs text-emerald-600 font-normal"><CheckCircle className="w-3 h-3" /> Connected</span>
                            : <span className="flex items-center gap-1 text-xs text-amber-600 font-normal"><WifiOff className="w-3 h-3" /> Not connected</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {destinations.includes("google_drive") && gDriveFolderPath
                          ? <span className="text-primary font-medium truncate block">☁️ {gDriveFolderPath} <span className="text-muted-foreground font-normal">(click to change)</span></span>
                          : "Click to choose a Google Drive folder"}
                      </div>
                    </div>
                  </div>

                  {/* Connection / folder row — shown when google_drive is selected */}
                  {destinations.includes("google_drive") && (
                    <div className="px-3 pb-3 pt-0 space-y-3">
                      {!isGDriveConnected ? (
                        /* Not connected — show prominent connect button */
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="default"
                            className="gap-1.5 h-7 text-xs"
                            onClick={handleGDriveConnect}
                          >
                            <Cloud className="w-3.5 h-3.5" />
                            Connect Google Drive
                          </Button>
                        </div>
                      ) : (
                        <>
                          {/* Folder picker row — Location selector is inside the picker modal */}
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setGDrivePickerOpen(true)}
                              className="flex items-center gap-1.5 text-xs text-primary hover:underline font-medium"
                            >
                              <FolderOpen className="w-3.5 h-3.5" />
                              {gDriveFolderPath ? "Change folder" : "Choose folder…"}
                            </button>
                            {gDriveFolderPath && (
                              <button
                                type="button"
                                onClick={removeGDriveDestination}
                                className="ml-auto text-xs text-muted-foreground hover:text-destructive"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          {gDriveFolderPath && (
                            <p className="text-xs text-muted-foreground">
                              Saving to: <span className="font-medium text-foreground">{gDriveFolderPath}</span>
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Local Drive ── */}
                <div
                  className={cn(
                    "rounded-xl border transition-all",
                    destinations.includes("local")
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  )}
                >
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer"
                    onClick={() => {
                      if (destinations.includes("local") && isFsaSupported) {
                        // Card body click when already set: re-open folder picker
                        pickLocalFolder();
                      } else if (!destinations.includes("local")) {
                        toggleDestination("local");
                      }
                    }}
                  >
                    <Checkbox
                      checked={destinations.includes("local")}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          removeLocalDestination();
                        } else {
                          toggleDestination("local");
                        }
                      }}
                      disabled={folderPickerLoading}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <HardDrive className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">Local Drive</div>
                      <div className="text-xs text-muted-foreground">
                        {destinations.includes("local") && localFolderPath
                          ? <span className="text-primary font-medium truncate block">📁 {localFolderPath} <span className="text-muted-foreground font-normal">(click to change)</span></span>
                          : isFsaSupported
                            ? "Click to choose a folder on your device"
                            : "Files will download to your default Downloads folder (Firefox / Safari)"}
                      </div>
                    </div>
                    {folderPickerLoading && (
                      <svg className="animate-spin w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                  </div>

                  {/* Folder path row — shown when local is selected */}
                  {destinations.includes("local") && (
                    <div className="flex items-center gap-2 px-3 pb-3 pt-0">
                      {isFsaSupported ? (
                        <>
                          <button
                            type="button"
                            onClick={() => pickLocalFolder()}
                            disabled={folderPickerLoading}
                            className="flex items-center gap-1.5 text-xs text-primary hover:underline font-medium"
                          >
                            <FolderOpen className="w-3.5 h-3.5" />
                            {localFolderPath ? "Change folder" : "Choose folder…"}
                          </button>
                          {localFolderPath && (
                            <button
                              type="button"
                              onClick={removeLocalDestination}
                              className="ml-auto text-xs text-muted-foreground hover:text-destructive"
                            >
                              Remove
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">
                            Your browser doesn't support folder selection (Firefox / Safari). Files will be downloaded individually to your <strong>Downloads</strong> folder with the correct renamed labels.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            For direct folder saving, use <strong>Chrome</strong> or <strong>Edge</strong>.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Returning Patient Form */}
      {mode === "returning" && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Select Existing {subjectLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Searchable subject dropdown */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {subjectLabel} <span className="text-destructive">*</span>
              </Label>

              {/* Location filter — scopes the patient lookup to one region (or "All") */}
              {isGDriveConnected && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Location
                  </p>
                  <div className="flex gap-1.5 flex-wrap">
                    {(["Iowa", "Minnesota", "Colorado", "all"] as const).map((loc) => {
                      const isSelected = returningLocationFilter === loc;
                      const label = loc === "all" ? "All Locations" : loc;
                      // Each location gets a hardcoded brand colour so the
                      // chips stay consistent regardless of the page's --primary
                      // override (e.g. the Provider A-Roll page remaps --primary
                      // to Iowa Blue, which previously made Colorado read as
                      // the same colour as Iowa).
                      const selectedStyle =
                        loc === "Iowa"
                          ? "border-transparent bg-[#5B7C99] text-white ring-2 ring-[#5B7C99]"
                          : loc === "Minnesota"
                            ? "border-transparent bg-[#6A5ACD] text-white ring-2 ring-[#6A5ACD]"
                            : loc === "all"
                              ? "border-transparent bg-[#DC2626] text-white ring-2 ring-[#DC2626]"
                              : "border-transparent bg-[#1b6e4a] text-white ring-2 ring-[#1b6e4a]";
                      return (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => {
                            const next = isSelected ? null : loc;
                            setReturningLocationFilter(next);
                            setSelectedPatient(null);
                            setSearchQuery("");
                            // Keep the dropdown closed until the user focuses the search field.
                            setShowDropdown(false);
                          }}
                          className={cn(
                            "flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition-all",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            isSelected
                              ? selectedStyle
                              : "border-border bg-background hover:bg-muted/40 text-foreground",
                          )}
                        >
                          {isSelected && <Check className="w-3 h-3" />}
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {returningLocationFilter === "all" && (
                    <p className="text-[11px] text-muted-foreground">
                      Searching every region — slower; pick a single Location for faster loads.
                    </p>
                  )}
                </div>
              )}

              <div className="relative" ref={patientDropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={
                      isGDriveConnected && !returningLocationFilter
                        ? "Choose a Location above to search…"
                        : `Search ${subjectLabelLower}s by name...`
                    }
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                      if (!e.target.value) setSelectedPatient(null);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    disabled={isGDriveConnected && !returningLocationFilter}
                    className="pl-9 h-10"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedPatient(null);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Drive-connected: show patients from the selected Location(s) */}
                {isGDriveConnected && showDropdown && !selectedPatient && (
                  <div className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground border border-border rounded-xl shadow-lg overflow-hidden max-h-96 overflow-y-auto">
                    {!returningLocationFilter ? (
                      <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                        Pick a Location above to load {subjectLabelLower}s.
                      </div>
                    ) : isDrivePatientsLoading ? (
                      <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading {subjectLabelLower}s from Google Drive...
                      </div>
                    ) : drivePatients.length > 0 ? (
                      <>
                        {drivePatients.map((dp) => {
                          const permissionStyle =
                            dp.permission === "pre-approved"
                              ? "bg-emerald-100 text-emerald-700"
                              : dp.permission === "check-before-posting"
                                ? "bg-[#4986e7]/10 text-[#4986e7]"
                                : "bg-red-100 text-red-700";
                          const hasBadges = !!dp.permissionFolderName || dp.specialInstructions;
                          return (
                          <button
                            key={`${dp.region}-${dp.folderId}`}
                            onClick={() => selectDrivePatient(dp)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors text-left"
                          >
                            <div className="w-8 h-8 rounded-full bg-[#4986e7]/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-[#4986e7]">
                                {dp.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{dp.name}</div>
                              {hasBadges && (
                                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                  {dp.permissionFolderName && (
                                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium", permissionStyle)}>
                                      {dp.permissionFolderName}
                                    </span>
                                  )}
                                  {dp.specialInstructions && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#ff7537]/15 text-[#ff7537]">
                                      **Special Instructions
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full flex-shrink-0">
                              {dp.region}
                            </span>
                          </button>
                        );
                      })}
                        <div className="sticky bottom-0 bg-muted/80 backdrop-blur-sm border-t border-border px-4 py-1.5 text-xs text-muted-foreground text-center">
                          {drivePatients.length} {subjectLabelLower}{drivePatients.length !== 1 ? 's' : ''} — type to filter
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                        {searchQuery
                          ? `No ${subjectLabelLower}s found matching "${searchQuery}"`
                          : `No ${subjectLabelLower} folders found in Drive`}
                      </div>
                    )}
                  </div>
                )}

                {/* When Drive is not connected, show a prompt to connect */}
                {!isGDriveConnected && showDropdown && searchQuery && (
                  <div className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground border border-border rounded-xl shadow-lg p-4 text-center text-sm text-muted-foreground">
                    Connect Google Drive above to search for returning {subjectLabelLower}s
                  </div>
                )}
              </div>

              {selectedPatient && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-xl border border-primary/20">
                    <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary-foreground">
                        {selectedPatient.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{selectedPatient.name}</div>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {selectedPatient.region && (
                          <Badge variant="secondary" className="text-xs">
                            {selectedPatient.region}
                          </Badge>
                        )}
                        {selectedPatient.destinations && (selectedPatient.destinations as string[]).map((d) => (
                          <Badge key={d} variant="secondary" className="text-xs">
                            {d === "local" ? "Local Drive" : "Google Drive"}
                          </Badge>
                        ))}
                      </div>
                      {selectedPatient.gDriveFolderPath && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <Cloud className="w-3 h-3 inline mr-1" />
                          <span className="font-medium text-foreground">{selectedPatient.gDriveFolderPath}</span>
                        </div>
                      )}
                      {selectedNasPath && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <HardDrive className="w-3 h-3 inline mr-1" />
                          <a
                            href={selectedNasPath.href}
                            className="font-medium text-foreground hover:text-primary hover:underline break-all"
                            title="Open this NAS share in Finder"
                          >
                            {selectedNasPath.display}
                          </a>
                        </div>
                      )}
                      {selectedPatient.localFolderLabel && (
                        <div className="text-xs text-muted-foreground mt-1">
                          📁 Last saved to: <span className="font-medium text-foreground">{selectedPatient.localFolderLabel}/{selectedPatient.name}/</span>
                        </div>
                      )}
                    </div>
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  </div>
                </div>
              )}
            </div>

            {/* Associated Provider(s) — patient workflow only. Sits between
                the Patient selection and the Exam toggle. */}
            {!isProvider && (
              <ProviderAssociationField value={selectedProviders} onChange={setSelectedProviders} />
            )}

            {/* Session date auto-detects from each file's metadata in the
                next step. Exam toggle still flips the " - exam" suffix
                (patient flow); Provider flow uses the Video Topic field
                below instead to append " - {topic}". */}
            <ExamFootageToggle isExam={isExam} onExamChange={setIsExam} showExam={!isProvider} />
            <VideoTopicField
              videoTopic={videoTopic}
              onVideoTopicChange={setVideoTopic}
              showTopic={isProvider}
            />
            <FilmedByField
              value={filmedByUserId}
              onChange={setFilmedByUserId}
              options={filmersQuery.data ?? []}
              currentUserId={user?.googleId}
              loading={filmersQuery.isLoading}
            />

            {selectedPatient && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Upload Destinations <span className="text-destructive">*</span>
                </Label>
                {!isProvider && (
                  <p className="text-xs text-muted-foreground">
                    For each destination, choose the patient&apos;s <strong>name folder</strong>, not a{" "}
                    {yearFoldersEnabled ? "year or date" : "date"} folder. New footage is filed into the right{" "}
                    {yearFoldersEnabled ? "year and date folders" : "date folder"} automatically.
                  </p>
                )}

                {/* ── Google Drive ── */}
                <div className={cn(
                  "rounded-xl border transition-all",
                  returningDestinations.includes("google_drive") ? "border-primary bg-primary/5" : "border-border"
                )}>
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer"
                    onClick={() => {
                      if (returningDestinations.includes("google_drive") && isGDriveConnected) {
                        // Card body click when already set: re-open folder picker
                        setGDrivePickerOpen(true);
                      } else if (!returningDestinations.includes("google_drive")) {
                        setReturningDestinations((prev) => [...prev, "google_drive"]);
                      }
                    }}
                  >
                    <Checkbox
                      checked={returningDestinations.includes("google_drive")}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          setReturningDestinations((prev) => prev.filter((d) => d !== "google_drive"));
                          setReturningGDriveFolderId("");
                          setReturningGDriveFolderPath("");
                        } else {
                          setReturningDestinations((prev) => [...prev, "google_drive"]);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Cloud className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        Google Drive
                        {returningDestinations.includes("google_drive") && (
                          isGDriveConnected
                            ? <span className="flex items-center gap-1 text-xs text-emerald-600 font-normal"><CheckCircle className="w-3 h-3" /> Connected</span>
                            : <span className="flex items-center gap-1 text-xs text-amber-600 font-normal"><WifiOff className="w-3 h-3" /> Not connected</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {returningGDriveFolderPath
                          ? <span className="text-primary font-medium truncate block">☁️ {returningGDriveFolderPath} <span className="text-muted-foreground font-normal">(click to change)</span></span>
                          : selectedPatient.gDriveFolderPath
                            ? <span>Last used: <span className="font-medium text-foreground">{selectedPatient.gDriveFolderPath}</span></span>
                            : "No Drive folder saved for this patient"}
                      </div>
                    </div>
                  </div>
                  {returningDestinations.includes("google_drive") && (
                    <div className="px-3 pb-3 pt-0 space-y-2">
                      {!isGDriveConnected ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="default"
                          className="gap-1.5 h-7 text-xs"
                          onClick={handleGDriveConnect}
                        >
                          <Cloud className="w-3.5 h-3.5" />
                          Connect Google Drive
                        </Button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setGDrivePickerOpen(true)}
                          className="flex items-center gap-1.5 text-xs text-primary hover:underline font-medium"
                        >
                          <FolderOpen className="w-3.5 h-3.5" />
                          {returningGDriveFolderPath ? "Change folder" : "Choose folder…"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Local Drive ── */}
                <div className={cn(
                  "rounded-xl border transition-all",
                  returningDestinations.includes("local") ? "border-primary bg-primary/5" : "border-border"
                )}>
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer"
                    onClick={() => {
                      if (returningDestinations.includes("local") && isFsaSupported) {
                        // Card body click when already set: re-open folder picker
                        setReturningFolderPickerLoading(true);
                        window.showDirectoryPicker!({ mode: "readwrite" })
                          .then(async (handle) => {
                            setReturningLocalFolderHandle(handle);
                            setReturningLocalFolderPath(handle.name);
                            setPendingPersistedHandle(null);
                            toast.success(`Folder selected: ${handle.name}/`);
                            const folderId = selectedPatient?.gDriveFolderId;
                            if (folderId) {
                              await saveHandle(subjectLocalHandleKey(folderId), handle).catch(() => {});
                            }
                          })
                          .catch(() => {})
                          .finally(() => setReturningFolderPickerLoading(false));
                      } else if (!returningDestinations.includes("local")) {
                        setReturningDestinations((prev) => [...prev, "local"]);
                      }
                    }}
                  >
                    <Checkbox
                      checked={returningDestinations.includes("local")}
                      // Provider A-Roll requires Local Drive — the user picks
                      // the destination fresh every session and files save
                      // flat there, so we don't let them opt out.
                      disabled={isProvider}
                      onCheckedChange={(checked) => {
                        if (isProvider) return;
                        if (!checked) {
                          setReturningDestinations((prev) => prev.filter((d) => d !== "local"));
                          setReturningLocalFolderHandle(null);
                          setReturningLocalFolderPath("");
                        } else {
                          setReturningDestinations((prev) => [...prev, "local"]);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <HardDrive className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        Local Drive{isProvider && <span className="text-destructive ml-1">*</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {returningLocalFolderPath
                          ? (isProvider
                              ? <span className="text-primary font-medium truncate block">📁 {returningLocalFolderPath}/ <span className="text-muted-foreground font-normal">(click to change)</span></span>
                              : <span className="text-primary font-medium truncate block">📁 {returningLocalFolderPath}/{selectedPatient.name}/ <span className="text-muted-foreground font-normal">(click to change)</span></span>)
                          : isProvider
                            ? (isFsaSupported ? "Click to choose a folder for this session (files save directly here)" : "Files will download to your Downloads folder")
                            : selectedPatient.localFolderLabel
                              ? <span className="text-muted-foreground">Last used: <span className="font-medium text-foreground">{selectedPatient.localFolderLabel}/{selectedPatient.name}/</span></span>
                              : isFsaSupported ? "Click to choose a folder on your device" : "Files will download to your Downloads folder"}
                      </div>
                      {pendingPersistedHandle && !returningLocalFolderPath && (
                        <div className="mt-1.5">
                          <button
                            type="button"
                            disabled={reconnectInFlight}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!pendingPersistedHandle) return;
                              setReconnectInFlight(true);
                              try {
                                const perm = pendingPersistedHandle.requestPermission
                                  ? await pendingPersistedHandle.requestPermission({ mode: "readwrite" })
                                  : "denied";
                                if (perm === "granted") {
                                  setReturningLocalFolderHandle(pendingPersistedHandle);
                                  setReturningLocalFolderPath(pendingPersistedHandle.name);
                                  setReturningDestinations((prev) =>
                                    prev.includes("local") ? prev : [...prev, "local"],
                                  );
                                  toast.success(`Reconnected to ${pendingPersistedHandle.name}/`);
                                  setPendingPersistedHandle(null);
                                } else {
                                  toast.error("Permission denied — pick the folder manually");
                                  const folderId = selectedPatient?.gDriveFolderId;
                                  if (folderId) {
                                    await deleteHandle(subjectLocalHandleKey(folderId)).catch(() => {});
                                  }
                                  setPendingPersistedHandle(null);
                                }
                              } catch {
                                setPendingPersistedHandle(null);
                              } finally {
                                setReconnectInFlight(false);
                              }
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                          >
                            {reconnectInFlight ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <CheckCircle className="w-3.5 h-3.5" />
                            )}
                            Reconnect saved folder: <span className="font-semibold">{pendingPersistedHandle.name}/</span>
                          </button>
                        </div>
                      )}
                      {selectedNasPath && !returningLocalFolderPath && (
                        <div className="text-xs text-muted-foreground mt-1 break-all">
                          <span className="font-medium">NAS:</span>{" "}
                          <a
                            href={selectedNasPath.href}
                            onClick={(e) => e.stopPropagation()}
                            className="text-foreground hover:text-primary hover:underline"
                            title="Open this NAS share in Finder — then pick that folder above"
                          >
                            {selectedNasPath.display}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                  {returningDestinations.includes("local") && (
                    <div className="px-3 pb-3 pt-0">
                      {isFsaSupported ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={returningFolderPickerLoading}
                            onClick={async () => {
                              setReturningFolderPickerLoading(true);
                              try {
                                const handle = await window.showDirectoryPicker!({ mode: "readwrite" });
                                setReturningLocalFolderHandle(handle);
                                setReturningLocalFolderPath(handle.name);
                                setPendingPersistedHandle(null);
                                toast.success(`Folder selected: ${handle.name}/`);
                                const folderId = selectedPatient.gDriveFolderId;
                                if (folderId) {
                                  await saveHandle(subjectLocalHandleKey(folderId), handle).catch(() => {});
                                }
                              } catch {
                                // user cancelled
                              } finally {
                                setReturningFolderPickerLoading(false);
                              }
                            }}
                            className="flex items-center gap-1.5 text-xs text-primary hover:underline font-medium"
                          >
                            <FolderOpen className="w-3.5 h-3.5" />
                            {returningLocalFolderPath ? "Change folder" : "Choose folder…"}
                          </button>
                          {returningFolderPickerLoading && (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Firefox / Safari: files will download to your <strong>Downloads</strong> folder. Use Chrome or Edge for direct folder saving.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {returningDestinations.length === 0 && (
                  <p className="text-xs text-destructive">Please select at least one upload destination.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Proceed Button */}
      <Button
        onClick={() => void handleProceed()}
        disabled={isLoading || consentUploading || isProceeding}
        className="w-full h-12 text-base font-semibold"
      >
        {(isProceeding || isLoading) ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {isLoading ? "Setting up session..." : "Creating folders…"}
          </span>
        ) : (
          "Proceed to Upload →"
        )}
      </Button>
    </div>
  );
}

// ─── Associated Provider(s) field ─────────────────────────────────────────────
// Patient-workflow-only field that associates one or more providers with the
// session's footage. Expected before proceeding — handleProceed pops a
// confirm (add one / skip) when it's empty. Thin wrapper around the shared ProviderPicker (which owns
// the cross-location autocomplete + colour-coded results + chips). Selected
// names ride through to footage_files.providers (Phase 3).

function ProviderAssociationField({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium flex items-center gap-1.5">
        <Users className="w-4 h-4 text-muted-foreground" />
        Associated Provider(s) <span className="text-destructive">*</span>
      </Label>
      <ProviderPicker value={value} onChange={onChange} />
    </div>
  );
}

// ─── Exam toggle ──────────────────────────────────────────────────────────────
// Standalone replacement for the old date-picker section. Session date is
// auto-detected from each file's metadata in FootageStep, so the only
// decision left here is whether the footage is exam footage (which suffixes
// the auto-detected folder name with " - exam"). Provider mode never had
// the Exam toggle, so the whole section is hidden there.

interface ExamFootageToggleProps {
  isExam: boolean;
  onExamChange: (val: boolean) => void;
  showExam?: boolean;
}

function ExamFootageToggle({ isExam, onExamChange, showExam = true }: ExamFootageToggleProps) {
  if (!showExam) return null;
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Exam</Label>
      <div
        className="flex items-center gap-2 cursor-pointer select-none w-fit"
        onClick={() => onExamChange(!isExam)}
      >
        <Checkbox
          id="exam-footage"
          checked={isExam}
          onCheckedChange={(checked) => onExamChange(!!checked)}
          onClick={(e) => e.stopPropagation()}
        />
        <label htmlFor="exam-footage" className="text-sm cursor-pointer">
          Exam Footage
        </label>
        <span className="text-xs text-muted-foreground">
          (suffixes folder name with " - exam")
        </span>
      </div>
    </div>
  );
}

// ─── Video Topic field (Provider A-Roll only) ─────────────────────────────────
// Free-text suffix appended to the auto-detected MM.DD.YY folder name so a
// provider can group recordings by topic, e.g. "05.22.26 - Primitive Reflexes".
// FootageStep applies the suffix when it materializes the date folder(s);
// sanitization (stripping path-illegal characters) also happens there.

interface VideoTopicFieldProps {
  videoTopic: string;
  onVideoTopicChange: (val: string) => void;
  showTopic?: boolean;
}

function VideoTopicField({ videoTopic, onVideoTopicChange, showTopic = true }: VideoTopicFieldProps) {
  if (!showTopic) return null;
  const trimmed = videoTopic.trim();
  return (
    <div className="space-y-2">
      <Label htmlFor="video-topic" className="text-sm font-medium">
        Video Topic <span className="text-muted-foreground text-xs font-normal">(optional)</span>
      </Label>
      <Input
        id="video-topic"
        type="text"
        placeholder="e.g. Primitive Reflexes"
        value={videoTopic}
        onChange={(e) => onVideoTopicChange(e.target.value)}
        className="h-10"
        autoComplete="off"
      />
      <p className="text-xs text-muted-foreground">
        {trimmed
          ? <>Folder name: <span className="font-mono font-medium text-foreground">MM.DD.YY - {trimmed}</span></>
          : "Optional umbrella label appended to the date folder. Per-clip Topic Tags are suggested separately after upload."}
      </p>
    </div>
  );
}

// ─── Filmed By field (all uploads) ────────────────────────────────────────────
// Attributes the batch to the staff member who filmed it. Defaults to the
// logged-in user; can be reassigned when someone uploads on another's behalf.
// FootageStep stamps the choice onto every footage row (uploaded_by_*), feeding
// the "Filmed by" filter in Footage Search.
interface FilmedByFieldProps {
  value: string;
  onChange: (userId: string) => void;
  options: Array<{ googleId: string; name: string }>;
  currentUserId?: string;
  loading?: boolean;
}

function FilmedByField({ value, onChange, options, currentUserId, loading }: FilmedByFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="filmed-by" className="text-sm font-medium">
        Filmed by
      </Label>
      <Select value={value || undefined} onValueChange={onChange} disabled={loading}>
        <SelectTrigger id="filmed-by" className="h-10">
          <SelectValue placeholder={loading ? "Loading…" : "Select who filmed this"} />
        </SelectTrigger>
        <SelectContent>
          {options.map((u) => (
            <SelectItem key={u.googleId} value={u.googleId}>
              {u.name}
              {u.googleId === currentUserId ? " (you)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Defaults to you. Change it if you're uploading footage someone else filmed.
      </p>
    </div>
  );
}

// ─── Shared Date Picker (kept for any future manual-override use) ─────────────

interface DatePickerFieldProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  calendarOpen: boolean;
  setCalendarOpen: (open: boolean) => void;
  isExam: boolean;
  onExamChange: (val: boolean) => void;
  /** Whether to render the "Exam Footage" checkbox below the date picker. Defaults to true. */
  showExam?: boolean;
}

function DatePickerField({ selectedDate, onDateChange, calendarOpen, setCalendarOpen, isExam, onExamChange, showExam = true }: DatePickerFieldProps) {
  const dateLabel = formatDateFolder(selectedDate) + (isExam ? " - exam" : "");
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        Session Date <span className="text-destructive">*</span>
      </Label>
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full h-10 justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            {selectedDate ? (
              <span>
                <span className="font-medium">{formatDateFolder(selectedDate)}</span>
                <span className="text-muted-foreground ml-2 text-xs">
                  ({format(selectedDate, "MMMM d, yyyy")})
                </span>
              </span>
            ) : (
              "Select date"
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              if (date) {
                onDateChange(date);
                setCalendarOpen(false);
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {/* Exam Footage checkbox */}
      {showExam && (
        <div
          className="flex items-center gap-2 cursor-pointer select-none w-fit"
          onClick={() => onExamChange(!isExam)}
        >
          <Checkbox
            id="exam-footage"
            checked={isExam}
            onCheckedChange={(checked) => onExamChange(!!checked)}
            onClick={(e) => e.stopPropagation()}
          />
          <label htmlFor="exam-footage" className="text-sm cursor-pointer">
            Exam Footage
          </label>
        </div>
      )}
      {selectedDate && (
        <p className="text-xs text-muted-foreground">
          Subfolder: <span className="font-medium text-foreground">{dateLabel}/</span>
        </p>
      )}
    </div>
  );
}
